import React from "react";
import { Button, Chip, Grid, Input, InputProps } from "@material-ui/core";
import { Publish } from "@material-ui/icons";

type RenderFunction = (props: {
  fileName: string;
  handleDelete: () => void;
}) => React.ReactNode;

type FileInputProps = {
  name?: string;
  value?: File[] | "" | null;
  onChange?: InputProps["onChange"];
  onBlur?: InputProps["onBlur"];
  readOnly?: boolean;
  disabled?: boolean;
  id?: string;
  multiple?: boolean;
  buttonPosition?: "aboveFiles" | "belowFiles";
  children?: RenderFunction;
  lineBreak?: boolean;
  title?: string;
  accept?: string;
  dataTest?: string;
};

export const FileInput = React.forwardRef<unknown, FileInputProps>(
  (
    {
      name,
      value,
      onChange,
      readOnly,
      disabled,
      id,
      multiple,
      buttonPosition = "aboveFiles",
      children = defaultRenderer,
      lineBreak,
      title,
      accept,
      dataTest,
    },
    ref
  ) => {
    const existingFiles = value || [];

    const internalOnChange: InputProps["onChange"] = (event: any) => {
      if (!onChange) {
        return;
      }
      const nextValue = [...existingFiles, ...(event?.target?.files || [])];
      onChange({
        ...event,
        target: {
          ...event.target,
          value: nextValue.length ? nextValue : null,
        },
      });
    };

    const onDelete = (val: File) => {
      if (!onChange) {
        return;
      }
      const nextValue = existingFiles.filter((file) => file !== val);

      onChange(({
        type: true,
        name,
        target: {
          value: nextValue.length ? nextValue : null,
        },
      } as unknown) as React.ChangeEvent<HTMLInputElement>);
    };

    const inputId = id ? id : `file-input-${name}`;
    const isSingleAndEmpty = !multiple && (!value || !value.length);

    const button = (
      <div>
        <label htmlFor={inputId}>
          {multiple || isSingleAndEmpty ? (
            <Button
              color="primary"
              component="span"
              disabled={disabled || readOnly}
              style={{ marginTop: 5 }}
              data-test={dataTest}
            >
              {title ? title : multiple ? "Select Files..." : "Select File..."}
              <Publish />
            </Button>
          ) : null}
        </label>
      </div>
    );

    const attachedFiles = existingFiles.map((file) => {
      const content = children({
        fileName: file.name,
        handleDelete: () => onDelete && onDelete(file),
      });
      const key = `${file.name}_${file.size}`;
      return lineBreak ? (
        <div key={key}>{content}</div>
      ) : (
        <React.Fragment key={key}>{content}</React.Fragment>
      );
    });

    return (
      <>
        <Input
          id={inputId}
          type="file"
          inputProps={{ multiple, accept }}
          hidden
          style={{ display: "none" }}
          name={name}
          onChange={internalOnChange}
          readOnly={readOnly}
          disabled={disabled}
        />
        {buttonPosition === "aboveFiles" && (
          <>
            {button}
            {attachedFiles}
          </>
        )}
        {buttonPosition === "belowFiles" && (
          <>
            {attachedFiles}
            {button}
          </>
        )}
      </>
    );
  }
);

const defaultRenderer: RenderFunction = ({ fileName, handleDelete }) => (
  <>
    <Chip
      color="primary"
      label={fileName}
      onDelete={handleDelete}
      onMouseDown={(event: any) => {
        event.stopPropagation();
      }}
      role="listitem"
      style={{ marginTop: 5 }}
    />
    &nbsp;
  </>
);
